import { clsx, type ClassValue } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"

// The seven type-scale classes tailwind.config.ts emits from design-tokens `typography` (fontSize: typeScale).
// Default tailwind-merge files any unknown `text-*` under text colour, so `text-meta text-gray-500` in one cn()
// call lost `text-meta`. Registered as font sizes they keep their colour and replace text-sm / text-2xl etc.
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: ['page-title', 'page-subtitle', 'section-title', 'body', 'meta', 'caption', 'kpi'] }],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Generate a secure random password
 * @param length - Length of password (default: 16)
 * @param includeSpecial - Include special characters (default: true)
 * @returns Secure random password
 */
export function generateSecurePassword(length: number = 16, includeSpecial: boolean = true): string {
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const lowercase = 'abcdefghijklmnopqrstuvwxyz'
  const numbers = '0123456789'
  const special = '!@#$%^&*()_+-=[]{}|;:,.<>?'
  
  let charset = uppercase + lowercase + numbers
  if (includeSpecial) {
    charset += special
  }
  
  // Ensure at least one character from each required category
  let password = ''
  password += uppercase[Math.floor(Math.random() * uppercase.length)]
  password += lowercase[Math.floor(Math.random() * lowercase.length)]
  password += numbers[Math.floor(Math.random() * numbers.length)]
  if (includeSpecial) {
    password += special[Math.floor(Math.random() * special.length)]
  }
  
  // Fill the rest randomly
  for (let i = password.length; i < length; i++) {
    password += charset[Math.floor(Math.random() * charset.length)]
  }
  
  // Shuffle the password
  return password.split('').sort(() => Math.random() - 0.5).join('')
}

/**
 * Generate email pattern from mailbox name
 * @param firstName - First name
 * @param lastName - Last name
 * @param domain - Domain name
 * @returns Email address pattern
 */
export function generateEmailPattern(firstName: string, lastName: string, domain: string): string {
  const first = firstName?.toLowerCase().trim() || ''
  const last = lastName?.toLowerCase().trim() || ''
  
  if (first && last) {
    return `${first}.${last}@${domain}`
  } else if (first) {
    return `${first}@${domain}`
  } else if (last) {
    return `${last}@${domain}`
  }
  
  return `user@${domain}`
}

/**
 * Generate 3 email variations for a mailbox
 * @param firstName - First name
 * @param lastName - Last name
 * @param domain - Domain name
 * @returns Array of 3 email variations
 */
export function generateEmailVariations(firstName: string, lastName: string, domain: string): string[] {
  const first = firstName?.toLowerCase().trim() || ''
  const last = lastName?.toLowerCase().trim() || ''
  const firstInitial = first[0] || ''
  
  const variations: string[] = []
  
  if (first && last) {
    // Variation 1: firstname@domain (e.g., max@trycruva.com)
    variations.push(`${first}@${domain}`)
    
    // Variation 2: firstname.lastname@domain (e.g., max.cho@trycruva.com)
    variations.push(`${first}.${last}@${domain}`)
    
    // Variation 3: f.lastname@domain or firstnamelastname@domain (e.g., m.cho@trycruva.com or maxcho@trycruva.com)
    // Prefer f.lastname format
    if (firstInitial) {
      variations.push(`${firstInitial}.${last}@${domain}`)
    } else {
      variations.push(`${first}${last}@${domain}`)
    }
  } else if (first) {
    // Only first name - use same pattern 3 times with slight variations
    variations.push(`${first}@${domain}`)
    variations.push(`${first}1@${domain}`)
    variations.push(`${first}2@${domain}`)
  } else if (last) {
    // Only last name
    variations.push(`${last}@${domain}`)
    variations.push(`${last}1@${domain}`)
    variations.push(`${last}2@${domain}`)
  } else {
    // No name - use generic patterns
    variations.push(`user@${domain}`)
    variations.push(`user1@${domain}`)
    variations.push(`user2@${domain}`)
  }
  
  return variations
}

/**
 * Generate signature template with actual values
 * @param fullName - Full name of the mailbox
 * @param title - Title/position
 * @param companyName - Company name
 * @returns Formatted signature string
 */
export function generateSignature(fullName: string, title: string, companyName: string): string {
  const parts: string[] = []
  
  if (fullName) {
    parts.push(fullName)
  }
  
  if (title) {
    parts.push(title)
  }
  
  if (companyName) {
    parts.push(companyName)
  }
  
  return parts.join('\n')
}
